import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios'
import {
  BACKEND_URL,
  PORTAL_TOKEN_NAME,
  REFRESH_TOKEN,
} from './constants/apiConstants'
import {getApiBaseUrl} from './apiBaseUrl'
import {getCookies, removeCookies, setCookies} from './utils/cookiesUtil'
import {assertAdminUiHost, getIsAdminUI, getLoginUrl, urlUtil} from './utils'
import {useAppStore} from './store/appStore'

/**
 * One axios instance, surface resolved per request.
 *
 * Admin talks to the `/admin/*` route tree and authenticates with the backend's
 * httpOnly session cookie — `withCredentials` is what attaches it, and there is no
 * `Authorization` header at all. Portal keeps its own paths, its bearer token and
 * `withCredentials: false`. Nothing in a request says which surface it belongs to any
 * more; the URL does.
 *
 * `isAdminUI`/`baseURL`/`withCredentials` are resolved inside the request interceptor,
 * not cached at module scope — `getIsAdminUI()` reads `window.location`, which can
 * change after this module first loads (client-side navigation, or a test's
 * `setWindowLocation()`), and a cached value would silently keep serving whichever
 * surface was live at import time.
 */

assertAdminUiHost()

export const apiClient: AxiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
})

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const isAdminUI = getIsAdminUI()
    config.baseURL = getApiBaseUrl()
    config.withCredentials = isAdminUI

    // Portal only. The admin session cookie is httpOnly and travels on its own.
    if (!isAdminUI) {
      const token = getCookies(PORTAL_TOKEN_NAME)
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`
      }
    }

    const storeState = useAppStore.getState()
    const workforceID =
      storeState.selectedWorkforce?.workforceID ||
      storeState.currentUser?.workforceID

    if (workforceID) {
      config.headers['workforceid'] = String(workforceID)
    }

    const portalOrigin = localStorage.getItem('wfPortalOrigin')
    if (portalOrigin) {
      config.headers['wfportalorigin'] = portalOrigin
    }

    return config
  },
  error => Promise.reject(error),
)

// Response Interceptor
apiClient.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as
      (InternalAxiosRequestConfig & {_retry?: boolean}) | undefined

    if (!originalRequest) {
      return Promise.reject(error)
    }

    // 🔹 Retry once if 502 happens
    if (error.response?.status === 502 && !originalRequest._retry) {
      originalRequest._retry = true
      return apiClient(originalRequest)
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      const redirectToLogin = (): void => {
        // Only one of N concurrent 401s should navigate — the rest are no-ops. Stored in
        // useAppStore (not a local flag) so BaseLayout can react to it and suppress the
        // "Unauthorized" flash while this redirect is in flight — window.location.href
        // doesn't halt React rendering, so the current page keeps painting until the
        // browser actually unloads.
        if (useAppStore.getState().isRedirectingToLogin) return
        // Already on the auth callback — interrupting here causes an infinite redirect loop.
        if (window.location.pathname.includes('/authorization/callback')) return
        useAppStore.getState().setIsRedirectingToLogin(true)
        removeCookies(PORTAL_TOKEN_NAME)
        removeCookies(REFRESH_TOKEN)
        window.location.href = getLoginUrl()
      }

      // Admin: the session and its refresh both live on the server, so a 401 means the
      // session is genuinely gone and the client has nothing to retry with. Straight to
      // a new OAuth round trip, which returns the caller to this same page.
      if (getIsAdminUI()) {
        redirectToLogin()
        return Promise.reject(error)
      }

      // Portal: rotate via refresh token. `panelId` comes from the loaded user — it used
      // to be read from a `panelId` cookie that nothing in this codebase ever wrote, so
      // it was always NaN and this path always fell through to a login redirect.
      const refreshToken = getCookies(REFRESH_TOKEN)
      const panelId = useAppStore.getState().currentUser?.panelId

      if (refreshToken && panelId) {
        try {
          const {data} = await axios.post(
            urlUtil.joinUrl(BACKEND_URL ?? '', 'auth/refresh-token'),
            {refresh_token: refreshToken, panelId},
          )

          if (data?.access_token) {
            setCookies(REFRESH_TOKEN, data.refresh_token)
            setCookies(PORTAL_TOKEN_NAME, data.access_token)
            originalRequest.headers['Authorization'] =
              `Bearer ${data.access_token}`
            return apiClient(originalRequest)
          }
        } catch (refreshError) {
          console.error(refreshError, 'Token refresh failed')
          redirectToLogin()
          return Promise.reject(refreshError)
        }
      }

      redirectToLogin()
    }

    return Promise.reject(error)
  },
)
